// @flow
import BluebirdPromise from 'bluebird';

import * as r from './resources';
import { get, post } from './http';

import {
  serializedQuikContactListQuery,
  serializedQuikOrderListQuery,
  serializedQuikOrderDetailsQuery,
  serializedQuikShipmentListQuery,
  serializedQuikOpenItemsListQuery,
  serializedQuikPaymentListQuery,
  serializedQuikPaymentDetailsQuery,
  serializedQuikShipmentDetailsQuery,
  serializedQuikPendingOrderListQuery,
} from 'modules/quik-order-history';
import type {
  QuikContactListQuery,
  QuikOrderListQuery,
  QuikPendingOrderListQuery,
  QuikOrderDetailsQuery,
  QuikShipmentListQuery,
  QuikOpenItemsListQuery,
  QuikPaymentListQuery,
  QuikPaymentDetailsQuery,
  QuikShipmentDetailsQuery,
} from 'modules/quik-order-history';
import type { Dispatch, GetState } from '~/store';

export const quikPendingOrderList = (
  query: QuikPendingOrderListQuery,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  get(r.quikPendingOrderList, serializedQuikPendingOrderListQuery(query));

export const quikOrderList = (
  query: QuikOrderListQuery,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  post(r.quikOrderList, '', serializedQuikOrderListQuery(query));

export const quikShipmentList = (
  query: QuikShipmentListQuery,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  post(r.quikShipmentList, '', serializedQuikShipmentListQuery(query));

export const quikOpenItemsList = (
  query: QuikOpenItemsListQuery,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  post(r.quikOpenItemsList, '', serializedQuikOpenItemsListQuery(query));

export const quikReturnList = (
  query: QuikOrderListQuery,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  post(r.quikOrderList, '', serializedQuikOrderListQuery(query));

export const quikPaymentList = (
  query: QuikPaymentListQuery,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  post(r.quikPaymentList, '', serializedQuikPaymentListQuery(query));

export const quikContactList = (
  query: QuikContactListQuery,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  post(r.quikContactList, '', serializedQuikContactListQuery(query));

export const quikOrderDetails = (
  query: QuikOrderDetailsQuery,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  post(r.quikOrderDetails, '', serializedQuikOrderDetailsQuery(query));

export const quikShipmentDetails = (
  query: QuikShipmentDetailsQuery,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  post(r.quikShipmentDetails, '', serializedQuikShipmentDetailsQuery(query));

export const quikPaymentDetails = (
  query: QuikPaymentDetailsQuery,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  post(r.quikPaymentDetails, '', serializedQuikPaymentDetailsQuery(query));

// The link runner uses the same cache busting technique as implemented in s4.
// https://github.com/ElasticSuite/scramble4/commit/e0e002ec65401cb194ab2a762a699076edb75544
export const getUrl = (link: string): Promise<Response> =>
  new BluebirdPromise((resolve, reject, onCancel) => {
    const controller = new AbortController();
    if (onCancel) onCancel(() => controller.abort());
    return fetch(`${link}${link.includes('?') ? '&' : '?'}x-request=fetch`, {
      signal: controller.signal,
    })
      .then(resolve)
      .catch(reject);
  });

// takes a list of links and returns a promise containing a list of the files
export const retrieveFilesFromUrls = (
  links: $ReadOnlyArray<string>,
  onProgress: (total: number, completed: number) => void,
): BluebirdPromise<$ReadOnlyArray<Blob>> => {
  // eslint-disable-next-line fp/no-let
  let completed = 0;

  return BluebirdPromise.map(
    links,
    async (link) => {
      const response = await getUrl(link);
      const blob = await response.blob();
      // eslint-disable-next-line fp/no-mutation
      completed += 1;
      onProgress(links.length, completed);

      return blob;
    },
    { concurrency: 6 },
  );
};
